/**
 * Agents API
 *
 * Handles all agent performance and coaching-related API calls.
 * Uses Zod validation for response types.
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import type { EmployeeIssuesResponse, FeedbackEffectResponse } from '@aica/api-types';
import type { FeedbackEffectParams, ReportWindowParams } from './reports.api';
import {
  agentPerformanceResponseSchema,
  teamLeaderboardResponseSchema,
  meCompetitionResponseSchema,
  coachingInsightsResponseSchema,
  weeklyCoachingPlanResponseSchema,
  supervisorNotesResponseSchema,
  myCoachingSessionsResponseSchema,
  supervisorNoteResponseSchema,
  teamMetricsResponseSchema,
  qualityDistributionResponseSchema,
  scriptAdherenceResponseSchema,
  objectionsResponseSchema,
  objectionStreamResponseSchema,
  dashboardConfigEventsResponseSchema,
  employeeIssuesResponseSchema,
  feedbackEffectResponseSchema,
} from '@aica/api-types/schemas';
import type {
  AgentPerformanceResponse,
  TeamLeaderboardResponse,
  MeCompetitionResponse,
  CoachingInsightsResponse,
  WeeklyCoachingPlanResponse,
  SupervisorNotesResponse,
  MyCoachingSessionsResponse,
  SupervisorNoteResponse,
  CoachingSessionsResponse,
  CoachingSession,
} from '../types';
import type {
  TeamMetricsResponse,
  QualityDistributionResponse,
  ScriptAdherenceResponse,
  ObjectionsResponse,
  ObjectionStreamResponse,
  CallCategory,
  DashboardConfigEventsResponse,
  TeamLeaderboardOrder,
  TeamLeaderboardScope,
  AgentScreenIncludeDemo,
} from '@aica/api-types/schemas';

export class AgentsApi extends BaseApiClient {
  // ============================================================================
  // AGENT PERFORMANCE
  // ============================================================================

  /**
   * Get agent performance - validated with Zod
   */
  async getAgentPerformance(params?: {
    period?: 'today' | 'week' | 'month' | 'quarter';
    periodStart?: string;
    periodEnd?: string;
    /** The screen's demo policy (`useDemoFilterFromAuth`), as the quality block receives it. */
    includeDemo?: AgentScreenIncludeDemo;
    /** The unified dashboard's project filter (#1338 PR C); none — every project. */
    projectIds?: string[];
  }): Promise<AgentPerformanceResponse> {
    // `buildQuery`, а не `URLSearchParams`: последний превращает
    // необязательный `periodStart: undefined` в литеральную строку
    // «undefined», которую `z.string().datetime()` на сервере отвергает — и
    // панель молча получает `null` вместо данных. `buildQuery` такой ключ
    // пропускает (`base.ts`: `if (value === undefined) continue`).
    const query = this.buildQuery(params);
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_ME_PERFORMANCE}${query}`,
      agentPerformanceResponseSchema
    );
  }

  /**
   * Свои просадки в «Моё» (#767) — те же числа, что у руководителя в
   * «Команде» (`ReportsApi.getEmployeeIssues`), по своим записям.
   */
  async getMyIssues(params: ReportWindowParams): Promise<EmployeeIssuesResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_ME_ISSUES}${this.buildQuery(params)}`,
      employeeIssuesResponseSchema
    );
  }

  /**
   * Свой блок «После фидбека» в «Моё» (#2609) — та же сборка, что у
   * руководителя в «Команде» (`ReportsApi.getEmployeeFeedbackEffect`).
   */
  async getMyFeedbackEffect(
    params: FeedbackEffectParams
  ): Promise<FeedbackEffectResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_ME_FEEDBACK_EFFECT}${this.buildQuery(params)}`,
      feedbackEffectResponseSchema
    );
  }

  /**
   * Get specific agent's performance by ID - validated with Zod
   */
  async getAgentPerformanceById(
    agentId: string,
    params?: {
      period?: 'today' | 'week' | 'month' | 'quarter';
      periodStart?: string;
      periodEnd?: string;
      includeDemo?: AgentScreenIncludeDemo;
      /** The portal of a CRM employee without an account (#1927); none — the server picks. */
      installation?: string;
    }
  ): Promise<AgentPerformanceResponse> {
    // `buildQuery`, а не `URLSearchParams`: последний превращает
    // необязательный `periodStart: undefined` в литеральную строку
    // «undefined», которую `z.string().datetime()` на сервере отвергает — и
    // панель молча получает `null` вместо данных. `buildQuery` такой ключ
    // пропускает (`base.ts`: `if (value === undefined) continue`).
    const query = this.buildQuery(params);
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_BY_ID_PERFORMANCE(agentId)}${query}`,
      agentPerformanceResponseSchema
    );
  }

  /**
   * Get competition leaderboard for current agent - validated with Zod
   * Accessible to agents (unlike teams/leaderboard which requires supervisor+)
   */
  async getMeCompetition(params?: {
    period?: 'today' | 'week' | 'month' | 'quarter';
    periodStart?: string;
    periodEnd?: string;
    limit?: number;
  }): Promise<MeCompetitionResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_ME_COMPETITION}${this.buildQuery(params)}`,
      meCompetitionResponseSchema
    );
  }

  /**
   * Get team leaderboard - validated with Zod
   */
  async getTeamLeaderboard(params?: {
    period?: 'today' | 'week' | 'month' | 'quarter';
    periodStart?: string;
    periodEnd?: string;
    limit?: number;
    offset?: number;
    /** `asc` — the low end first; `position` is still the rank from the top. */
    order?: TeamLeaderboardOrder;
    /** `team` — every scored agent in one response; `limit`/`offset` ignored (#1140). */
    scope?: TeamLeaderboardScope;
    /** Empty/omitted = all projects the caller may see. */
    projectIds?: string[];
    /** The screen's demo policy (`useDemoFilterFromAuth`), as the quality block receives it (#1355). */
    includeDemo?: AgentScreenIncludeDemo;
  }): Promise<TeamLeaderboardResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_TEAMS_LEADERBOARD}${this.buildQuery(params)}`,
      teamLeaderboardResponseSchema
    );
  }

  // ============================================================================
  // AI COACHING
  // ============================================================================

  /**
   * Get coaching insights - validated with Zod
   */
  async getCoachingInsights(params?: {
    period?: 'today' | 'week' | 'month' | 'quarter';
    periodStart?: string;
    periodEnd?: string;
    /** The screen's demo policy (`useDemoFilterFromAuth`), as the quality block receives it. */
    includeDemo?: AgentScreenIncludeDemo;
  }): Promise<CoachingInsightsResponse> {
    // `buildQuery`, а не `URLSearchParams`: последний превращает
    // необязательный `periodStart: undefined` в литеральную строку
    // «undefined», которую `z.string().datetime()` на сервере отвергает — и
    // панель молча получает `null` вместо данных. `buildQuery` такой ключ
    // пропускает (`base.ts`: `if (value === undefined) continue`).
    const query = this.buildQuery(params);
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_ME_COACHING_INSIGHTS}${query}`,
      coachingInsightsResponseSchema
    );
  }

  /**
   * Советы по КОНКРЕТНОМУ агенту — для супервизорского вида страницы.
   *
   * Пока метода не было, страница супервизора звала `/me/coaching/insights` и
   * показывала рядом с карточкой агента СВОИ СОБСТВЕННЫЕ советы: сильные
   * стороны смотрящего, его пункты плана и ссылки на его записи. Маршрут
   * `/:agentId/coaching/insights` на бэкенде существовал и резолвил
   * идентичность, но потребителя у него не было ни одного.
   */
  async getCoachingInsightsById(
    agentId: string,
    params?: {
      period?: 'today' | 'week' | 'month' | 'quarter';
      periodStart?: string;
      periodEnd?: string;
      includeDemo?: AgentScreenIncludeDemo;
      /** The portal of a CRM employee without an account (#1927). */
      installation?: string;
    }
  ): Promise<CoachingInsightsResponse> {
    // `buildQuery`, а не `URLSearchParams`: последний превращает
    // необязательный `periodStart: undefined` в литеральную строку
    // «undefined», которую `z.string().datetime()` на сервере отвергает — и
    // панель молча получает `null` вместо данных. `buildQuery` такой ключ
    // пропускает (`base.ts`: `if (value === undefined) continue`).
    const query = this.buildQuery(params);
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_BY_ID_COACHING_INSIGHTS(agentId)}${query}`,
      coachingInsightsResponseSchema
    );
  }

  /**
   * Get weekly coaching plan - validated with Zod
   */
  async getWeeklyCoachingPlan(params?: {
    includeDemo?: AgentScreenIncludeDemo;
  }): Promise<WeeklyCoachingPlanResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_ME_COACHING_WEEKLY}${this.buildQuery(params)}`,
      weeklyCoachingPlanResponseSchema
    );
  }

  /**
   * Get notes sent by me (Supervisor POV)
   */
  async getSentNotes(params?: {
    limit?: number;
    offset?: number;
  }): Promise<SupervisorNotesResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_ME_COACHING_NOTES_SENT}${this.buildQuery(params)}`,
      supervisorNotesResponseSchema
    );
  }

  /**
   * Get supervisor notes for me (agent POV)
   */
  async getSupervisorNotes(params?: {
    limit?: number;
    offset?: number;
  }): Promise<SupervisorNotesResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_ME_COACHING_NOTES}${this.buildQuery(params)}`,
      supervisorNotesResponseSchema
    );
  }

  /**
   * My coaching meetings in `[start, end)` (agent POV, #2053): where I am the
   * one being coached — never the meetings I lead.
   */
  async getMyCoachingSessions(params: {
    start: string;
    end: string;
    limit?: number;
    offset?: number;
  }): Promise<MyCoachingSessionsResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_ME_COACHING_SESSIONS}${this.buildQuery(params)}`,
      myCoachingSessionsResponseSchema
    );
  }

  /**
   * Get notes for a specific agent (Supervisor POV)
   */
  async getAgentNotes(
    agentId: string,
    params?: {
      limit?: number;
      offset?: number;
    }
  ): Promise<SupervisorNotesResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_BY_ID_COACHING_NOTES(agentId)}${this.buildQuery(params)}`,
      supervisorNotesResponseSchema
    );
  }

  /**
   * Create supervisor note - validated with Zod
   */
  async createSupervisorNote(data: {
    agentId: string;
    content: string;
    recordId?: string;
  }): Promise<SupervisorNoteResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.AGENTS_COACHING_NOTES,
      supervisorNoteResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Get coaching sessions
   */
  async getCoachingSessions(params?: {
    limit?: number;
    offset?: number;
    status?: 'scheduled' | 'completed' | 'cancelled';
  }): Promise<CoachingSessionsResponse> {
    return this.request(
      `${API_ENDPOINTS.AGENTS_COACHING_SESSIONS}${this.buildQuery(params)}`
    );
  }

  /**
   * Create coaching session
   */
  async createCoachingSession(data: {
    agentId: string;
    scheduledAt: string;
    notes?: string;
    recordId?: string;
    sourceAlertEventId?: string;
  }): Promise<{ session: CoachingSession }> {
    return this.request(API_ENDPOINTS.AGENTS_COACHING_SESSIONS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get coaching stats (for supervisor dashboard). `includeDemo: 'no'` leaves
   * the demo's coaching out (#2294); absent — the server's default, with it.
   */
  async getCoachingStats(params: { includeDemo?: AgentScreenIncludeDemo } = {}): Promise<{
    totalNotesSent: number;
    notesThisWeek: number;
    pendingFollowUps: number;
    averageResponseTime: number;
  }> {
    return this.request(
      `${API_ENDPOINTS.AGENTS_COACHING_STATS}${this.buildQuery(params)}`
    );
  }

  // ============================================================================
  // ANALYTICS
  // ============================================================================

  /**
   * Get team metrics breakdown - validated with Zod
   */
  async getTeamMetrics(params?: {
    period?: 'today' | 'week' | 'month' | 'quarter';
    periodStart?: string;
    periodEnd?: string;
    /** @deprecated pass `projectIds` — ignored whenever it is present and non-empty. */
    projectId?: string;
    /** Multi-project filter (project-multiselect K7). Empty/omitted = all projects. */
    projectIds?: string[];
  }): Promise<TeamMetricsResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_TEAM_METRICS}${this.buildQuery(params)}`,
      teamMetricsResponseSchema
    );
  }

  /**
   * Get config-change / model-drift markers for quality trends (WS7-T14).
   * `tzOffset` (minutes east of UTC) buckets event days to the caller's local
   * day so markers land on the same x-tick as the trend points.
   */
  async getConfigEvents(params?: {
    period?: 'today' | 'week' | 'month' | 'quarter';
    periodStart?: string;
    periodEnd?: string;
    projectId?: string;
    tzOffset?: number;
  }): Promise<DashboardConfigEventsResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_CONFIG_EVENTS}${this.buildQuery(params)}`,
      dashboardConfigEventsResponseSchema
    );
  }

  /**
   * Get quality distribution - validated with Zod
   */
  async getQualityDistribution(params?: {
    period?: 'today' | 'week' | 'month' | 'quarter';
    periodStart?: string;
    periodEnd?: string;
    /** @deprecated pass `projectIds` — ignored whenever it is present and non-empty. */
    projectId?: string;
    /** Multi-project filter (project-multiselect K7). Empty/omitted = all projects. */
    projectIds?: string[];
  }): Promise<QualityDistributionResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_QUALITY_DISTRIBUTION}${this.buildQuery(params)}`,
      qualityDistributionResponseSchema
    );
  }

  /**
   * Get per-agent script adherence + org average - validated with Zod
   */
  async getScriptAdherence(params?: {
    periodStart?: string;
    periodEnd?: string;
    /** @deprecated pass `projectIds` — ignored whenever it is present and non-empty. */
    projectId?: string;
    /** Multi-project filter (project-multiselect K7). Empty/omitted = all projects. */
    projectIds?: string[];
    includeDemo?: 'yes' | 'no';
  }): Promise<ScriptAdherenceResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_SCRIPT_ADHERENCE}${this.buildQuery(params)}`,
      scriptAdherenceResponseSchema
    );
  }

  /**
   * Get the objection types the team most often fails to handle (WS7-T7) -
   * validated with Zod.
   */
  async getObjections(params?: {
    periodStart?: string;
    periodEnd?: string;
    /** @deprecated pass `projectIds` — ignored whenever it is present and non-empty. */
    projectId?: string;
    /** Multi-project filter (project-multiselect K7). Empty/omitted = all projects. */
    projectIds?: string[];
    /** The dashboard's demo policy (`useDemoFilterFromAuth`) - the card counts what its screen counts. */
    includeDemo?: 'yes' | 'no';
  }): Promise<ObjectionsResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_OBJECTIONS}${this.buildQuery(params)}`,
      objectionsResponseSchema
    );
  }

  /**
   * Every objection code of a window with the previous window (#846) - the
   * "Objections" screen. The window is capped at 93 days server-side.
   */
  async getObjectionStream(params: {
    periodStart: string;
    periodEnd: string;
    /** Empty/omitted = all projects the caller may see. */
    projectIds?: string[];
    callCategory?: CallCategory;
    /** The screen's demo policy (`useDemoFilterFromAuth`), as the dashboards send it. */
    includeDemo?: 'yes' | 'no';
    /** The Voice of Customer section's theme filter (#2098): the answer echoes it. */
    themeCode?: string;
    /**
     * Ask the server for the Р3 mark of each code («What changed», #2552): it
     * counts them only on request. Voice of Customer section only.
     */
    marks?: 'true';
  }): Promise<ObjectionStreamResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.AGENTS_OBJECTIONS_STREAM}${this.buildQuery(params)}`,
      objectionStreamResponseSchema
    );
  }
}
